import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Button — the one action primitive (ENG-APP-UX-TUNEUP Track D).
 *
 * Source: docs/product/experience-and-ui/48-in-app-design-spec.md § 2.1
 * Actions (ratified 2026-09-28) — it supersedes the earlier "ink fill" draft
 * of question 7: Primary is the ACCENT fill with an INK label (9.5 : 1),
 * Secondary white with a 1 px Neutral Gray border, Tertiary text only,
 * Destructive Error Text on white with a danger-soft edge, Destructive-confirm
 * the Error Text fill with a white label (5.6 : 1; inside its dialog only).
 * 36 px default / 32 px compact, 14 px / 600 label, 8 px radius
 * (BRANDING § 4.4), the shared 2 px `--color-primary-text` focus outline with
 * a 2 px offset (`:focus-visible` only, never a box-shadow). Hover is a colour
 * or border shift on the default transition tokens (BRANDING § 4.6).
 *
 * `loading` keeps the width, swaps the leading icon for a spinner and sets
 * `aria-busy`; the label stays so the result can be reported in place
 * ("Applying…" → "Applied", spec Rule 8). `type` defaults to "button" so a
 * Button inside a form never submits by accident.
 */
export type ButtonVariant =
  "primary" | "secondary" | "tertiary" | "destructive" | "destructiveConfirm";

export type ButtonSize = "default" | "compact";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** An icon drawn before the label (16 px, decorative). */
  leadingIcon?: ReactNode;
  /** In-flight state: spinner in the icon slot, `aria-busy`, no clicks. */
  loading?: boolean;
}

export const BUTTON_BASE =
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-text " +
  "disabled:cursor-not-allowed disabled:opacity-50 press";

export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  // Accent fill, ink label — 9.5 : 1 (spec § 2.1); hover is Primary Dark.
  primary: "bg-primary text-ink hover:bg-primary-dark",
  secondary:
    "border border-border bg-surface text-ink hover:border-ink-soft hover:bg-surface-alt",
  tertiary: "bg-transparent text-primary-text hover:underline",
  destructive:
    "border border-danger-soft bg-surface text-error-text hover:bg-danger-bg",
  // Inside its confirmation dialog only (spec § 2.1).
  destructiveConfirm: "bg-error-text text-white hover:bg-error",
};

export const BUTTON_SIZES: Record<ButtonSize, string> = {
  default: "h-9 px-4",
  compact: "h-8 px-3",
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "default",
    leadingIcon,
    loading = false,
    disabled,
    className,
    children,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(
        BUTTON_BASE,
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 size={16} className="animate-spin" aria-hidden="true" />
      ) : (
        leadingIcon
      )}
      {children}
    </button>
  );
});

export default Button;
